import React, { useEffect, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { IconButton } from './ui/IconButton';

export interface MediaItem {
  id?: string | number;
  url: string;
  thumbnailUrl?: string;
  type: 'image' | 'video';
  title?: string;
  description?: string;
  filename?: string;
  sizeBytes?: number;
  width?: number;
  height?: number;
  createdAt?: string;
  tags?: Array<{ id: number; tagValue: string; color?: string }>;
}

export interface MediaViewerProps {
  items: MediaItem[];
  currentIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (index: number) => void;
  customActions?: React.ReactNode;
  showMetadata?: boolean;
  showNavigation?: boolean;
  autoPlayVideo?: boolean;
  allowDownload?: boolean;
  className?: string;
}

function formatFileSize(bytes: number | undefined): string {
  if (!bytes || bytes === 0) return '';
  const k = 1024; const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const navBtnClass = "absolute top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white border-none w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-colors hover:bg-black/70 backdrop-blur-sm";

const MediaViewer: React.FC<MediaViewerProps> = ({
  items, currentIndex = 0, isOpen, onClose, onNavigate, customActions,
  showMetadata = true, showNavigation = true, autoPlayVideo = true, allowDownload = true, className = '',
}) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { setActiveIndex(currentIndex); }, [currentIndex]);
  useEffect(() => { setIsLoading(true); }, [activeIndex]);

  const currentItem = items[activeIndex];
  const hasMultipleItems = items.length > 1;
  const canNavigate = showNavigation && hasMultipleItems;

  const goToPrevious = useCallback(() => {
    const i = activeIndex > 0 ? activeIndex - 1 : items.length - 1;
    setActiveIndex(i); onNavigate?.(i);
  }, [activeIndex, items.length, onNavigate]);

  const goToNext = useCallback(() => {
    const i = activeIndex < items.length - 1 ? activeIndex + 1 : 0;
    setActiveIndex(i); onNavigate?.(i);
  }, [activeIndex, items.length, onNavigate]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && canNavigate) goToPrevious();
      else if (e.key === 'ArrowRight' && canNavigate) goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, canNavigate, goToPrevious, goToNext, onClose]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen || items.length === 0 || !currentItem) return null;

  const displayTitle = currentItem.title || currentItem.filename || 'Media';

  return (
    <div
      className={twMerge('fixed inset-0 bg-black/90 z-[10000] flex items-center justify-center', className)}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-label="Media viewer"
    >
      <div className="relative w-full h-full flex flex-col md:flex-row" onClick={e => e.stopPropagation()}>
        <IconButton
          onClick={onClose}
          aria-label="Close viewer"
          className="absolute top-[calc(1rem+env(safe-area-inset-top,0px))] right-4 z-20 bg-black/50 text-white w-10 h-10 rounded-full hover:bg-black/70 backdrop-blur-sm"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          }
        />

        {canNavigate && (
          <>
            <IconButton
              className={twMerge(navBtnClass, 'left-4')}
              onClick={goToPrevious}
              aria-label="Previous item"
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>}
            />
            <IconButton
              className={twMerge(navBtnClass, 'right-4 md:right-[320px]')}
              onClick={goToNext}
              aria-label="Next item"
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>}
            />
          </>
        )}

        <div className="flex-1 flex items-center justify-center p-4 relative">
          {isLoading && <div className="absolute inset-0 flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" /></div>}
          {currentItem.type === 'image' ? (
            <img src={currentItem.url} alt={displayTitle}
              className={twMerge('max-w-full max-h-full object-contain rounded-lg transition-opacity', isLoading ? 'opacity-0' : 'opacity-100')}
              onLoad={() => setIsLoading(false)} />
          ) : (
            <video src={currentItem.url}
              className={twMerge('max-w-full max-h-full rounded-lg transition-opacity', isLoading ? 'opacity-0' : 'opacity-100')}
              controls autoPlay={autoPlayVideo} onLoadedData={() => setIsLoading(false)} />
          )}
        </div>

        {showMetadata && (
          <div className="w-full md:w-[300px] bg-bg-secondary/95 backdrop-blur-sm p-5 overflow-y-auto border-t md:border-t-0 md:border-l border-border-light max-h-[40vh] md:max-h-full">
            <div className="flex items-start justify-between gap-2 mb-3">
              <h3 className="m-0 text-base font-semibold text-text-primary leading-snug">{displayTitle}</h3>
              {hasMultipleItems && <span className="text-xs text-text-secondary bg-bg-tertiary py-0.5 px-2 rounded-full shrink-0">{activeIndex + 1} / {items.length}</span>}
            </div>
            {currentItem.description && <p className="text-sm text-text-secondary leading-relaxed mb-3">{currentItem.description}</p>}
            <div className="flex flex-col gap-1 text-sm mb-3">
              {currentItem.type && <span className="text-text-secondary"><span className="text-text-tertiary">Type:</span> {currentItem.type}</span>}
              {currentItem.sizeBytes && <span className="text-text-secondary"><span className="text-text-tertiary">Size:</span> {formatFileSize(currentItem.sizeBytes)}</span>}
              {currentItem.width && currentItem.height && <span className="text-text-secondary"><span className="text-text-tertiary">Dimensions:</span> {currentItem.width} x {currentItem.height}</span>}
              {currentItem.createdAt && <span className="text-text-secondary"><span className="text-text-tertiary">Uploaded:</span> {formatDate(currentItem.createdAt)}</span>}
            </div>
            {currentItem.tags && currentItem.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {currentItem.tags.map(tag => (
                  <span key={tag.id} className="py-0.5 px-2 text-xs text-white rounded-full" style={{ backgroundColor: tag.color || 'var(--theme-color-default)' }}>{tag.tagValue}</span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 pt-3 border-t border-border-light">
              {allowDownload && (
                <a href={currentItem.url} download={currentItem.filename || displayTitle}
                  className="inline-flex items-center gap-1.5 py-1.5 px-3 bg-bg-tertiary text-text-primary border border-border-light rounded-lg text-sm no-underline transition-colors hover:bg-surface">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download
                </a>
              )}
              {customActions}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaViewer;
